/**
 * REST API service for RAG (Retrieval Augmented Generation) chat with Legal, TEBG, ITSM
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { IPublicClientApplication } from '@azure/msal-browser';

export type RestRagType = 'Legal' | 'DevOps' | 'TEBG' | 'ITSM' | 'EverydayInsights';

export interface RestRagResponseChunk {
  type: 'content' | 'citations' | 'done';
  data: {
    content: string;
  };
  error_data: any | null;
  done: any | null;
  source: string;
  error?: any; // error field (DevOps/Legal)
  citations?: any[]; // citations array (DevOps/Legal)
  citation_count?: number; // citation count (DevOps/Legal)
}

interface RagEndpoint {
  path: string;
  name: string;
  useApim: boolean; // Whether to use APIM base URL or direct endpoint
}

// Get RAG-specific APIM base URL from environment variable with fallback
// RAG endpoints may be routed differently than main chat API
const RAG_APIM_BASE_URL = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';

// TODO: move to a server-side proxy before production — client-side bundles expose this value
const EVERYDAY_INSIGHTS_SUBSCRIPTION_KEY = 'cea6b33b52ce4fc098b961b94972c248'; // local test only - do not commit

const RAG_ENDPOINTS: Record<RestRagType, RagEndpoint> = {
  Legal: {
    path: '/nav-rag-api/la-rag',
    name: 'LA',
    useApim: true,
  },
  DevOps: {
    path: '/nav-rag-api/devops-rag',
    name: 'DevOps',
    useApim: true,
  },
  TEBG: {
    path: 'http://internal-aicoe-rag-tebg-167146033.us-east-1.elb.amazonaws.com/query/stream',
    name: 'TEBG',
    useApim: false, // Direct AWS ELB endpoint
  },
  ITSM: {
    path: 'http://internal-aicoe--ragal-dyqhud0auraf-164638462.us-east-1.elb.amazonaws.com/query/stream',
    name: 'ITSM',
    useApim: false, // Direct AWS ELB endpoint
  },
  EverydayInsights: {
    path: 'https://ai-np-apim-main.mmm.com/everydayinsights',
    name: 'EverydayInsights',
    useApim: false, // Uses its own APIM URL with a dedicated subscription key
  },
};

/**
 * Get the full URL for a RAG endpoint
 */
const getRagEndpointUrl = (ragType: RestRagType): string => {
  const endpoint = RAG_ENDPOINTS[ragType];
  return endpoint.useApim ? `${RAG_APIM_BASE_URL}${endpoint.path}` : endpoint.path;
};

export class RestRagChatService {
  private ragType: RestRagType;
  private msalInstance: IPublicClientApplication;
  private abortController: AbortController | null = null;
  private onMessageCallback: ((chunk: RestRagResponseChunk) => void) | null = null;
  private onErrorCallback: ((error: Error) => void) | null = null;
  private onCompleteCallback: (() => void) | null = null;
  private onCompleteFired: boolean = false; // guard against double-firing (e.g. [DONE] + reader done)

  constructor(ragType: RestRagType, msalInstance: IPublicClientApplication) {
    this.ragType = ragType;
    this.msalInstance = msalInstance;
  }

  /**
   * Set callback for receiving message chunks
   */
  public onMessage(callback: (chunk: RestRagResponseChunk) => void): void {
    this.onMessageCallback = callback;
  }

  /**
   * Set callback for errors
   */
  public onError(callback: (error: Error) => void): void {
    this.onErrorCallback = callback;
  }

  /**
   * Set callback for completion
   */
  public onComplete(callback: () => void): void {
    this.onCompleteCallback = callback;
  }

  /**
   * Get the access token for API requests
   */
  private async getAccessToken(): Promise<string> {
    const accounts = this.msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    const request = {
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      // Fallback to interactive token acquisition
      const response = await this.msalInstance.acquireTokenPopup(request);
      return response.accessToken;
    }
  }

  /**
   * Send a query to the RAG service
   * @param query - The user's query text
   * @param chatHistory - Optional full chat history for multi-turn conversations
   */
  public async sendQuery(query: string, chatHistory?: Array<{role: string; content: string}>): Promise<void> {
    const endpoint = RAG_ENDPOINTS[this.ragType];
    const endpointUrl = getRagEndpointUrl(this.ragType);
    
    // Reset guard so a new request can fire onComplete
    this.onCompleteFired = false;
    
    // Helper to fire onComplete exactly once per request
    const fireComplete = () => {
      if (!this.onCompleteFired) {
        this.onCompleteFired = true;
        if (this.onCompleteCallback) {
          this.onCompleteCallback();
        }
      }
    };
    
    try {
      // Create new abort controller for this request
      this.abortController = new AbortController();

      // Get access token for APIM authentication
      const accessToken = await this.getAccessToken();

      // Build headers - add service-specific APIM headers if needed
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      };

      // Everyday Insights requires its own APIM subscription key
      if (this.ragType === 'EverydayInsights') {
        headers['Ocp-Apim-Subscription-Key'] = EVERYDAY_INSIGHTS_SUBSCRIPTION_KEY;
      }

      // Build request body - DevOps requires additional fields
      // Use full chat history if provided, otherwise fall back to single message with current query
      const chatHistoryToSend = chatHistory || [
        {
          role: 'user',
          content: query
        }
      ];
      
      const requestBody = (this.ragType === 'DevOps' || this.ragType === 'Legal' || this.ragType === 'EverydayInsights')
        ? { 
            query,
            request_id: `${this.ragType.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
            timestamp: new Date().toISOString(),
            query_type: 'chat',
            chat_history: chatHistoryToSend
          }
        : { query };

      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Process the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          fireComplete();
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });

        // Process complete Server-Sent Events (SSE) messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            // Data line - parse and emit
            const dataStr = line.substring(5).trim();
            
            if (dataStr) {
              // Check for [DONE] marker (DevOps RAG uses this)
              if (dataStr === '[DONE]') {
                fireComplete();
                continue;
              }

              try {
                const parsedData = JSON.parse(dataStr);
                
                // Convert DevOps event format to standard chunk format
                let chunk: RestRagResponseChunk;
                
                // DevOps sends streaming content as {"delta": {"content": "text"}}
                if (parsedData.delta && parsedData.delta.content !== undefined) {
                  chunk = {
                    type: 'content',
                    data: {
                      content: parsedData.delta.content,
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                  };
                }
                // DevOps completion with status: "done"
                else if (parsedData.status === 'done') {
                  chunk = {
                    type: 'done',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: parsedData,
                    source: endpoint.name,
                  };
                }
                // DevOps citations
                else if (parsedData.citations && Array.isArray(parsedData.citations)) {
                  chunk = {
                    type: 'citations',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                    ...parsedData,
                  };
                }
                // Filter out DevOps status updates (not content)
                else if (parsedData.status && ['retrieving', 'retrieved', 'reranking', 'reranked', 'generating'].includes(parsedData.status)) {
                  continue; // Skip status events
                }
                // Filter out DevOps retrieved chunk metadata (not streaming content)
                else if (parsedData.source && parsedData.content && parsedData.score !== undefined && parsedData.position !== undefined) {
                  continue; // Skip retrieved chunk metadata
                }
                else if (currentEventType === 'stream') {
                  // DevOps stream event - extract text content
                  const textContent = parsedData.content || parsedData.chunk || parsedData.text || '';
                  chunk = {
                    type: 'content',
                    data: {
                      content: textContent,
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                  };
                } else if (currentEventType === 'complete') {
                  // DevOps complete event - convert to done chunk
                  chunk = {
                    type: 'done',
                    data: {
                      content: parsedData.response || '',
                    },
                    error_data: null,
                    done: parsedData,
                    source: endpoint.name,
                  };
                } else if (currentEventType === 'citations') {
                  // DevOps citations event - convert to citations chunk
                  chunk = {
                    type: 'citations',
                    data: {
                      content: '',
                    },
                    error_data: null,
                    done: null,
                    source: endpoint.name,
                    ...parsedData, // Include citations array and citation_count
                  };
                } else if (currentEventType === 'error') {
                  // DevOps error event
                  chunk = {
                    type: 'content',
                    data: {
                      content: '',
                    },
                    error_data: parsedData,
                    done: null,
                    source: endpoint.name,
                    error: parsedData.error,
                  };
                } else if (parsedData.type) {
                  // Standard format with explicit type field
                  chunk = parsedData;
                } else {
                  continue; // Unknown format - skip
                }

                // Emit the chunk to the callback
                if (this.onMessageCallback) {
                  this.onMessageCallback(chunk);
                }
                
                // Reset event type after processing
                currentEventType = '';
              } catch (parseError) {
                console.error('[REST RAG] Error parsing SSE data:', parseError);
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        // Detect browser-level CORS / network failure (TypeError thrown before any HTTP
        // response is received — e.g. "Failed to fetch", "NetworkError when attempting
        // to fetch resource", "Load failed").
        const isCorsOrNetwork =
          error instanceof TypeError &&
          /(failed to fetch|networkerror|load failed)/i.test(error.message);
        const surfacedError = isCorsOrNetwork
          ? new Error(
              'Your request could not be completed. Please try again or try rephrasing your prompt. ' +
              'If this keeps happening, please contact support.'
            )
          : error;
        if (this.onErrorCallback) {
          this.onErrorCallback(surfacedError);
        }
        throw surfacedError;
      }
      throw error;
    }
  }

  /**
   * Cancel the current query
   */
  public cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    this.cancel();
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onCompleteCallback = null;
  }
}
